<template>
    <form-wrapper class="intake-prior" @submit="handleFormSubmission(data)">
        <intake-form-section :title="$t('forms.intake.previous_injuries.sections.prior_injuries')">
            <p v-if="!data.priorInjuries.length" class="intake-prior__empty">
                {{ $t('forms.intake.previous_injuries.empty') }}
            </p>

            <div v-for="(injury, index) in data.priorInjuries" :key="`prior-${index}`" class="intake-prior__card">
                <div class="intake-prior__card-header">
                    <h4 class="intake-prior__card-title">
                        {{ $t('forms.intake.previous_injuries.event_label', { number: index + 1 }) }}
                    </h4>
                    <common-button
                        :label="$t('common.microcopy.remove')"
                        type="secondary"
                        size="small"
                        :isSubmittable="false"
                        @click="removePriorInjury(index)"
                    ></common-button>
                </div>

                <form-row>
                    <form-cell>
                        <select-field
                            :label="$t('forms.intake.previous_injuries.fields.incident_type.label')"
                            :placeholder="$t('forms.intake.previous_injuries.fields.incident_type.placeholder')"
                            :isRequired="true"
                            :options="incidentTypeOptions"
                            :formData="data"
                            :valuePath="`priorInjuries.${index}.incidentType`"
                            @change="change($event)"
                        ></select-field>
                    </form-cell>
                </form-row>

                <form-row>
                    <form-cell>
                        <text-field
                            :label="$t('forms.intake.previous_injuries.fields.approximate_date.label')"
                            :placeholder="$t('forms.intake.previous_injuries.fields.approximate_date.placeholder')"
                            :formData="data"
                            :valuePath="`priorInjuries.${index}.approximateDate`"
                            @change="change($event)"
                        ></text-field>
                    </form-cell>
                </form-row>

                <form-row>
                    <form-cell>
                        <textarea-field
                            :label="$t('forms.intake.previous_injuries.fields.description.label')"
                            :placeholder="$t('forms.intake.previous_injuries.fields.description.placeholder')"
                            :formData="data"
                            :valuePath="`priorInjuries.${index}.description`"
                            @change="change($event)"
                        ></textarea-field>
                    </form-cell>
                </form-row>

                <div class="intake-prior__regions">
                    <label class="form-field__label">
                        {{ $t('forms.intake.previous_injuries.fields.body_regions.label') }}
                    </label>
                    <div class="intake-prior__regions-grid">
                        <checkbox-input
                            v-for="option in bodyRegionOptions"
                            :key="option.value"
                            :label="option.label"
                            :value="isBodyRegionChecked(index, option.value)"
                            @change="toggleBodyRegion(index, option.value)"
                        ></checkbox-input>
                    </div>
                </div>

                <form-row>
                    <form-cell>
                        <tabs-field
                            :label="$t('forms.intake.previous_injuries.fields.symptoms_resolved.label')"
                            :options="triStateOptions"
                            :formData="data"
                            :valuePath="`priorInjuries.${index}.symptomsResolved`"
                            size="small"
                            @change="change($event)"
                        ></tabs-field>
                    </form-cell>
                </form-row>

                <form-row>
                    <form-cell>
                        <textarea-field
                            :label="$t('forms.intake.previous_injuries.fields.lingering_symptoms.label')"
                            :placeholder="$t('forms.intake.previous_injuries.fields.lingering_symptoms.placeholder')"
                            :formData="data"
                            :valuePath="`priorInjuries.${index}.lingeringSymptoms`"
                            @change="change($event)"
                        ></textarea-field>
                    </form-cell>
                </form-row>
            </div>

            <button class="intake-prior__add" type="button" @click="addPriorInjury">
                + {{ $t('forms.intake.previous_injuries.actions.add_prior_injury') }}
            </button>
        </intake-form-section>

        <template #footer>
            <common-buttons>
                <common-button
                    :label="$t('common.microcopy.back')"
                    type="secondary"
                    size="large"
                    :isSubmittable="false"
                    @click="goBack"
                ></common-button>
                <common-button
                    :label="$t('common.microcopy.next')"
                    type="primary"
                    size="large"
                    :isProcessing="isSending"
                ></common-button>
            </common-buttons>
        </template>
    </form-wrapper>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import IntakeFormSection from 'components/forms/intake/IntakeFormSection'

    const BODY_REGIONS = [
        'head',
        'neck',
        'upper_back',
        'mid_back',
        'low_back',
        'right_shoulder',
        'left_shoulder',
        'right_arm',
        'left_arm',
        'right_elbow',
        'left_elbow',
        'right_wrist_hand',
        'left_wrist_hand',
        'chest',
        'abdomen',
        'right_hip',
        'left_hip',
        'right_thigh',
        'left_thigh',
        'right_knee',
        'left_knee',
        'right_leg',
        'left_leg',
        'right_ankle_foot',
        'left_ankle_foot',
        'other',
    ]

    const INCIDENT_TYPES = [
        'motor_vehicle',
        'construction',
        'other_workplace',
        'slip_trip_fall',
        'pedestrian_bicycle',
        'assault',
        'sports',
        'other',
    ]

    export default {
        mixins: [FormMixin],

        components: {
            IntakeFormSection,
        },

        props: {
            initialData: { type: Object, default: undefined },
        },

        data() {
            return {
                data: {
                    priorInjuries: [],
                    ...(this.initialData || {}),
                },
            }
        },

        computed: {
            incidentTypeOptions() {
                return INCIDENT_TYPES.map((value) => ({
                    label: this.$t(`forms.intake.what_happened.fields.incident_type.options.${value}`),
                    value,
                }))
            },

            bodyRegionOptions() {
                return BODY_REGIONS.map((value) => ({
                    label: this.$t(`forms.intake.how_you_felt.fields.body_region.options.${value}`),
                    value,
                }))
            },

            triStateOptions() {
                return ['yes', 'no', 'unsure'].map((value) => ({
                    label: this.$t(`forms.intake.what_happened.fields.tri_state.options.${value}`),
                    value,
                }))
            },
        },

        methods: {
            createPriorInjury() {
                return {
                    incidentType: undefined,
                    approximateDate: undefined,
                    description: undefined,
                    bodyRegions: [],
                    symptomsResolved: undefined,
                    lingeringSymptoms: undefined,
                }
            },

            addPriorInjury() {
                this.change({
                    path: 'priorInjuries',
                    value: [...this.data.priorInjuries, this.createPriorInjury()],
                })
            },

            removePriorInjury(index) {
                const next = [...this.data.priorInjuries]
                next.splice(index, 1)
                this.change({ path: 'priorInjuries', value: next })
            },

            isBodyRegionChecked(index, region) {
                const regions = this.data.priorInjuries[index]?.bodyRegions || []
                return regions.includes(region)
            },

            toggleBodyRegion(index, region) {
                const current = [...(this.data.priorInjuries[index]?.bodyRegions || [])]
                const nextRegions = current.includes(region) ? current.filter((item) => item !== region) : [...current, region]

                this.change({
                    path: `priorInjuries.${index}.bodyRegions`,
                    value: nextRegions,
                })
            },

            goBack() {
                this.emitEvent({ name: 'back', payload: this.data })
            },

            handleFormSubmission(data) {
                this.isSending = true
                this.emitSent(data)
                this.isSending = false
            },
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_functions.scss';
    @import 'styles/setup/_mixins.scss';

    .intake-prior__empty {
        margin: 0 0 rem(12px);
        font-size: rem($font-size--form);
        color: $color--txt-minor;
    }

    .intake-prior__card {
        margin-bottom: rem(16px);
        padding: rem(16px);
        border: rem(1px) solid $color--border-base;
        border-radius: rem($border-radius--base);
        background: $color--bg-dropdown;

        @include breakpoint(tablet) {
            padding: rem(12px);
        }
    }

    .intake-prior__card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: rem(12px);
        margin-bottom: rem(12px);
        flex-wrap: wrap;
    }

    .intake-prior__card-title {
        margin: 0;
        font-size: rem($font-size--form);
        font-weight: 600;
        color: $color--txt-header;
    }

    .intake-prior__regions {
        margin: rem(20px) 0;
    }

    .intake-prior__regions-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: rem(10px) rem(12px);
        margin-top: rem(10px);

        @include breakpoint(tablet) {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        @include breakpoint(smartphone) {
            grid-template-columns: 1fr;
        }
    }

    .intake-prior__add {
        display: block;
        width: 100%;
        margin-top: rem(4px);
        padding: rem(14px) rem(12px);
        border: rem(1px) dashed $color--accent-1;
        border-radius: rem($border-radius--base);
        background: transparent;
        color: $color--accent-1;
        font-size: rem($font-size--form);
        font-weight: 600;
        text-align: center;
        cursor: pointer;

        &:hover {
            background: rgba($color--accent-1, 0.06);
        }
    }
</style>
